import React, { useState, useEffect } from 'react';
import './MatchupDisplay.css';
import { useChampionDisplayNames } from '../../../utils/championNames';

interface Champion {
  id: string;
  name: string;
  level: number;
  teamColor: 'blue' | 'red';
}

interface Props {
  blueChampion?: Champion | null;
  redChampion?: Champion | null;
  className?: string;
  onChampionClick?: (champion: Champion) => void;
}

const MatchupDisplay: React.FC<Props> = ({ 
  blueChampion, 
  redChampion, 
  className = '',
  onChampionClick
}) => {
  const [apiVersion, setApiVersion] = useState<string>("15.9.1");
  const { getDisplayName } = useChampionDisplayNames();

  // Fetch the latest API version when component mounts
  useEffect(() => {
    const fetchApiVersion = async () => {
      try {
        const response = await fetch('https://ddragon.leagueoflegends.com/api/versions.json');
        if (response.ok) {
          const versions = await response.json();
          if (versions && versions.length > 0) {
            setApiVersion(versions[0]);
          }
        }
      } catch (err) {
        console.warn('Error fetching API version, using default:', err);
      }
    };
    
    fetchApiVersion();
  }, []);

  // Helper function to get champion image URL
  const getChampionImageUrl = (championName: string) => {
    return `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/champion/${championName}.png`;
  };
  return (
    <div className={`matchup-display ${className}`}>
      <div className="matchup-display__header">
        <div className="matchup-display__background">
          {/* Blue side gradient background */}
          <div className="matchup-display__team-background matchup-display__blue-background"></div>
          {/* Red side gradient background */}
          <div className="matchup-display__team-background matchup-display__red-background"></div>
        </div>
        
        <div className="matchup-display__container">
          {/* Blue Champion */}
          <div 
            className="matchup-display__champion-side matchup-display__blue-side"
            onClick={() => blueChampion && onChampionClick && onChampionClick(blueChampion)}
          >
            {blueChampion ? (
              <div className="matchup-display__champion-content">
                <div className="matchup-display__champion-portrait matchup-display__blue-portrait">
                  <img 
                    src={getChampionImageUrl(blueChampion.name)}
                    alt={blueChampion.name}
                    className="matchup-display__champion-image"
                    onError={(e) => {
                      // Fallback to placeholder on image error
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const placeholder = target.nextElementSibling as HTMLElement;
                      if (placeholder) {
                        placeholder.style.display = 'flex';
                      }
                    }}
                  />
                  <div className="matchup-display__champion-placeholder" style={{ display: 'none' }}>
                    {blueChampion.name.charAt(0)}
                  </div>
                </div>
                <div className="matchup-display__champion-info">
                  <div className="matchup-display__champion-name">{getDisplayName(blueChampion.name)}</div>
                </div>
              </div>
            ) : (
              <div className="matchup-display__empty-champion">
                <div className="matchup-display__empty-placeholder">No Champion</div>
              </div>
            )}
          </div>

          {/* VS Divider */}
          <div className="matchup-display__vs-divider">
            <span className="matchup-display__vs-text">VS</span>
          </div>

          {/* Red Champion */}
          <div 
            className="matchup-display__champion-side matchup-display__red-side"
            onClick={() => redChampion && onChampionClick && onChampionClick(redChampion)}
          >
            {redChampion ? (
              <div className="matchup-display__champion-content">
                <div className="matchup-display__champion-portrait matchup-display__red-portrait">
                  <img 
                    src={getChampionImageUrl(redChampion.name)}
                    alt={redChampion.name}
                    className="matchup-display__champion-image"
                    onError={(e) => {
                      // Fallback to placeholder on image error
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const placeholder = target.nextElementSibling as HTMLElement;
                      if (placeholder) {
                        placeholder.style.display = 'flex';
                      }
                    }}
                  />
                  <div className="matchup-display__champion-placeholder" style={{ display: 'none' }}>
                    {redChampion.name.charAt(0)}
                  </div>
                </div>
                <div className="matchup-display__champion-info">
                  <div className="matchup-display__champion-name">{getDisplayName(redChampion.name)}</div>
                </div>
              </div>
            ) : (
              <div className="matchup-display__empty-champion">
                <div className="matchup-display__empty-placeholder">No Champion</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchupDisplay;
